/**
 * LastSeenChip — small "Last seen N days ago" indicator surfacing
 * `user.last_login_at`. Colour shifts with churn risk:
 *   < 7d   → green dot, "active"
 *   7–30d  → amber dot, exact "N days ago" string
 *   > 30d  → red dot, "N days ago" + dimmer
 *   never  → grey dot, "Never signed in"
 */
export function LastSeenChip({ lastLoginAt }) {
  let label = 'Never signed in';
  let dotColor = 'bg-gray-300';
  let textColor = 'text-gray-400 italic';
  let testid = 'last-seen-never';

  if (lastLoginAt) {
    const last = new Date(lastLoginAt);
    if (!isNaN(last.getTime())) {
      const ms = Date.now() - last.getTime();
      const days = Math.floor(ms / (24 * 60 * 60 * 1000));
      const hours = Math.floor(ms / (60 * 60 * 1000));

      if (hours < 1) {
        label = 'Active just now';
        dotColor = 'bg-emerald-500';
        textColor = 'text-emerald-700';
        testid = 'last-seen-active';
      } else if (days < 1) {
        label = `${hours}h ago`;
        dotColor = 'bg-emerald-500';
        textColor = 'text-emerald-700';
        testid = 'last-seen-active';
      } else if (days < 7) {
        label = days === 1 ? 'Yesterday' : `${days} days ago`;
        dotColor = 'bg-emerald-500';
        textColor = 'text-emerald-700';
        testid = 'last-seen-recent';
      } else if (days < 30) {
        label = `${days} days ago`;
        dotColor = 'bg-amber-500';
        textColor = 'text-amber-700';
        testid = 'last-seen-stale';
      } else if (days < 365) {
        label = `${days} days ago`;
        dotColor = 'bg-red-500';
        textColor = 'text-red-700';
        testid = 'last-seen-churn';
      } else {
        const years = Math.floor(days / 365);
        label = `${years}y ago`;
        dotColor = 'bg-red-500';
        textColor = 'text-red-700';
        testid = 'last-seen-churn';
      }
    }
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs ${textColor}`}
      title={lastLoginAt ? new Date(lastLoginAt).toLocaleString() : 'No login recorded yet'}
      data-testid={testid}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
      {label}
    </span>
  );
}
